import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelpers, extractApiData } from '@/api/core';
import {
  type SiteResponse,
  type SiteInfoResponse,
  type CreateSiteRequest,
  type UpdateSiteRequest,
} from '@packages/types/site';
import {
  applySiteDetailToQueryCache,
  invalidateSiteRelatedQueries,
  patchSiteListsForCompany,
  removeSiteDetailFromQueryCache,
} from '@/api/cache/organizationMutationCache';
import { siteKeys } from './siteQueryKeys';

export { siteKeys } from './siteQueryKeys';

// 사이트 생성
export const useCreateSite = (companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (createData: CreateSiteRequest) => {
      const response = await apiHelpers.post<SiteResponse>('site.create', createData, {
        companyId,
      });
      return extractApiData(response);
    },
    onSuccess: (newSite: SiteResponse) => {
      patchSiteListsForCompany(queryClient, companyId, old => [...(old ?? []), newSite]);
      applySiteDetailToQueryCache(queryClient, newSite);
      invalidateSiteRelatedQueries(queryClient, companyId);
    },
  });
};

// 사이트 수정
export const useUpdateSite = (siteId: number, companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updateData: UpdateSiteRequest) => {
      const response = await apiHelpers.put<SiteResponse>('site.update', updateData, {
        id: siteId,
      });
      return extractApiData(response);
    },
    onSuccess: (updatedSite: SiteResponse) => {
      const sid = updatedSite.id;
      patchSiteListsForCompany(queryClient, companyId, old =>
        old ? old.map(s => (s.id === sid ? { ...s, ...updatedSite } : s)) : old
      );
      applySiteDetailToQueryCache(queryClient, updatedSite);
      invalidateSiteRelatedQueries(queryClient, companyId);
      // Site 정보 카드만 병합하면 siteManagers 배열이 남을 수 있음 → /info 재검증
      queryClient.invalidateQueries({ queryKey: siteKeys.info(sid) });
    },
  });
};

// 사이트 삭제 (비활성화) — 삭제할 siteId는 mutateAsync(siteId)로 전달
export const useDeleteSite = (companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (siteId: number) => {
      await apiHelpers.delete('site.delete', { id: siteId });
      return siteId;
    },
    onSuccess: (deletedId: number) => {
      patchSiteListsForCompany(queryClient, companyId, old =>
        old ? old.filter(s => s.id !== deletedId) : old
      );
      removeSiteDetailFromQueryCache(queryClient, deletedId);
      invalidateSiteRelatedQueries(queryClient, companyId);
    },
  });
};

// 특정 사이트 정보 및 매니저 정보 조회
export const useGetSiteInfo = (siteId: number, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: siteKeys.info(siteId),
    queryFn: async () => {
      const response = await apiHelpers.get<SiteInfoResponse>('site.getSiteInfo', { siteId });
      return extractApiData(response);
    },
    enabled: options?.enabled !== false && !!siteId,
  });
};
